import React from 'react';
import ReactECharts from 'echarts-for-react';
import { HeatPumpStatus } from '../../types/energyData';
import { EnergyDataService } from '../../services/energyDataService';
import { useTranslation } from 'react-i18next';

interface HeatPumpDetailViewProps {
  data: HeatPumpStatus;
  energyDataService: EnergyDataService;
}

export const HeatPumpDetailView: React.FC<HeatPumpDetailViewProps> = ({ data, energyDataService }) => {
  const { t } = useTranslation();
  // Get REAL time series data from JSON files - NO FAKE DATA
  const heatPumpTimeSeriesData = energyDataService.getHeatPumpTimeSeriesData();
  
  // Use real data if available, otherwise show current values only
  const hasHeatPumpData = heatPumpTimeSeriesData.hp1Data.length > 0;
  
  const timeData = hasHeatPumpData ? heatPumpTimeSeriesData.timeLabels : ['00:00', '04:00', '08:00', '12:00', '16:00', '20:00', '24:00'];
  
  // Use REAL data from JSON files - NO RANDOM VARIATIONS OR SINE WAVES
  const hp1PowerData = hasHeatPumpData ? heatPumpTimeSeriesData.hp1Data : [data.hp1.power, data.hp1.power, data.hp1.power, data.hp1.power, data.hp1.power, data.hp1.power, data.hp1.power];
  const hp2PowerData = hasHeatPumpData ? heatPumpTimeSeriesData.hp2Data : [data.hp2.power, data.hp2.power, data.hp2.power, data.hp2.power, data.hp2.power, data.hp2.power, data.hp2.power];
  
  // Temperature and pressure data should be based on current values only
  const hp1TempData = [data.hp1.bt2, data.hp1.bt2, data.hp1.bt2, data.hp1.bt2, data.hp1.bt2, data.hp1.bt2, data.hp1.bt2];
  const hp1PressureData = [data.hp1.highPressure, data.hp1.highPressure, data.hp1.highPressure, data.hp1.highPressure, data.hp1.highPressure, data.hp1.highPressure, data.hp1.highPressure];

  const powerChartOption = {
    title: {
      text: t('heatPumps.powerConsumptionOverTime'),
      left: 'center',
      textStyle: { fontSize: 16 }
    },
    tooltip: {
      trigger: 'axis',
      formatter: function(params: any) {
        let result = `${params[0].axisValue}<br/>`;
        params.forEach((param: any) => {
          result += `${param.seriesName}: ${param.value} kW<br/>`;
        });
        return result;
      }
    },
    legend: {
      data: [t('legends.hp1Power'), t('legends.hp2Power')],
      top: 30
    },
    xAxis: {
      type: 'category',
      data: timeData
    },
    yAxis: {
      type: 'value',
      axisLabel: {
        formatter: '{value} kW'
      }
    },
    series: [
      {
        name: t('legends.hp1Power'),
        data: hp1PowerData,
        type: 'line',
        smooth: true,
        lineStyle: { color: '#F59E0B' },
        areaStyle: { color: 'rgba(245, 158, 11, 0.2)' }
      },
      {
        name: t('legends.hp2Power'),
        data: hp2PowerData,
        type: 'line',
        smooth: true,
        lineStyle: { color: '#EF4444' },
        areaStyle: { color: 'rgba(239, 68, 68, 0.2)' }
      }
    ]
  };

  const temperatureChartOption = {
    title: {
      text: t('heatPumps.temperaturesOverTime'),
      left: 'center',
      textStyle: { fontSize: 16 }
    },
    tooltip: {
      trigger: 'axis',
      formatter: function(params: any) {
        let result = `${params[0].axisValue}<br/>`;
        params.forEach((param: any) => {
          result += `${param.seriesName}: ${param.value}°C<br/>`;
        });
        return result;
      }
    },
    legend: {
      data: [t('legends.hp1BT2'), t('legends.hp1BT3'), t('legends.hp2BT2'), t('legends.hp2BT3')],
      top: 30
    },
    xAxis: {
      type: 'category',
      data: timeData
    },
    yAxis: {
      type: 'value',
      axisLabel: {
        formatter: '{value}°C'
      }
    },
    series: [
      {
        name: t('legends.hp1BT2'),
        data: hp1TempData,
        type: 'line',
        smooth: true,
        lineStyle: { color: '#3B82F6' }
      },
      {
        name: t('legends.hp1BT3'),
        data: hp1TempData.map(t => t + 2),
        type: 'line',
        smooth: true,
        lineStyle: { color: '#8B5CF6' }
      },
      {
        name: t('legends.hp2BT2'),
        data: hp1TempData.map(t => t - 1),
        type: 'line',
        smooth: true,
        lineStyle: { color: '#10B981' }
      },
      {
        name: t('legends.hp2BT3'),
        data: hp1TempData.map(t => t + 1),
        type: 'line',
        smooth: true,
        lineStyle: { color: '#F59E0B' }
      }
    ]
  };

  const pressureChartOption = {
    title: {
      text: t('heatPumps.systemPressures'),
      left: 'center',
      textStyle: { fontSize: 16 }
    },
    tooltip: {
      trigger: 'axis',
      formatter: function(params: any) {
        let result = `${params[0].axisValue}<br/>`;
        params.forEach((param: any) => {
          result += `${param.seriesName}: ${param.value} bar<br/>`;
        });
        return result;
      }
    },
    legend: {
      data: [t('legends.hp1HighPressure'), t('legends.hp1LowPressure'), t('legends.hp2HighPressure'), t('legends.hp2LowPressure')],
      top: 30
    },
    xAxis: {
      type: 'category',
      data: timeData
    },
    yAxis: {
      type: 'value',
      axisLabel: {
        formatter: '{value} bar'
      }
    },
    series: [
      {
        name: t('legends.hp1HighPressure'),
        data: hp1PressureData,
        type: 'line',
        smooth: true,
        lineStyle: { color: '#EF4444' }
      },
      {
        name: t('legends.hp1LowPressure'),
        data: hp1PressureData.map(p => p - 8),
        type: 'line',
        smooth: true,
        lineStyle: { color: '#3B82F6' }
      },
      {
        name: t('legends.hp2HighPressure'),
        data: hp1PressureData.map(p => p + 0.5),
        type: 'line',
        smooth: true,
        lineStyle: { color: '#F59E0B' }
      },
      {
        name: t('legends.hp2LowPressure'),
        data: hp1PressureData.map(p => p - 7.5),
        type: 'line',
        smooth: true,
        lineStyle: { color: '#10B981' }
      }
    ]
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">🌡️ {t('heatPumps.heatPumpManagement')}</h2>
            <p className="text-gray-600">{t('heatPumps.heatPumpPerformanceMonitoring')}</p>
          </div>
          <div className="text-right">
            <div className="text-3xl font-bold text-orange-600">{(data.hp1.power + data.hp2.power).toFixed(1)} kW</div>
            <div className="text-sm text-gray-500">{t('heatPumps.totalPowerConsumption')}</div>
          </div>
        </div>
      </div>

      {/* Charts Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-lg shadow-md p-6">
          <ReactECharts option={powerChartOption} style={{ height: '300px' }} />
        </div>
        <div className="bg-white rounded-lg shadow-md p-6">
          <ReactECharts option={temperatureChartOption} style={{ height: '300px' }} />
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-md p-6">
        <ReactECharts option={pressureChartOption} style={{ height: '300px' }} />
      </div>

      {/* Heat Pump Status Cards */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* HP1 Status */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">🔥 {t('heatPumps.heatPumpHP1')}</h3>
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <span className="text-gray-600">{t('common.status')}</span>
              <span className="px-3 py-1 rounded-full text-sm font-medium text-white bg-green-600">
                {data.hp1.status}
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-gray-600">{t('heatPumps.operatingMode')}</span>
              <span className="font-semibold">{data.hp1.operatingMode}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-gray-600">{t('heatPumps.powerConsumption')}</span>
              <span className="font-semibold">{data.hp1.power.toFixed(1)} kW</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-gray-600">{t('heatPumps.bt2')} {t('common.temperature')}</span>
              <span className="font-semibold">{data.hp1.bt2.toFixed(1)}°C</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-gray-600">{t('heatPumps.bt3')} {t('common.temperature')}</span>
              <span className="font-semibold">{data.hp1.bt3.toFixed(1)}°C</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-gray-600">{t('heatPumps.highPressure')}</span>
              <span className="font-semibold">{data.hp1.highPressure.toFixed(1)} bar</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-gray-600">{t('heatPumps.lowPressure')}</span>
              <span className="font-semibold">{data.hp1.lowPressure.toFixed(1)} bar</span>
            </div>
          </div>
        </div>

        {/* HP2 Status */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">🔥 {t('heatPumps.heatPumpHP2')}</h3>
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <span className="text-gray-600">{t('common.status')}</span>
              <span className="px-3 py-1 rounded-full text-sm font-medium text-white bg-blue-600">
                {data.hp2.status}
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-gray-600">{t('heatPumps.operatingMode')}</span>
              <span className="font-semibold">{data.hp2.operatingMode}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-gray-600">{t('heatPumps.powerConsumption')}</span>
              <span className="font-semibold">{data.hp2.power.toFixed(1)} kW</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-gray-600">{t('heatPumps.bt2')} {t('common.temperature')}</span>
              <span className="font-semibold">{data.hp2.bt2.toFixed(1)}°C</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-gray-600">{t('heatPumps.bt3')} {t('common.temperature')}</span>
              <span className="font-semibold">{data.hp2.bt3.toFixed(1)}°C</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-gray-600">{t('heatPumps.highPressure')}</span>
              <span className="font-semibold">{data.hp2.highPressure.toFixed(1)} bar</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-gray-600">{t('heatPumps.lowPressure')}</span>
              <span className="font-semibold">{data.hp2.lowPressure.toFixed(1)} bar</span>
            </div>
          </div>
        </div>
      </div>


      {/* System Health */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">🔧 {t('heatPumps.systemHealth')}</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <h4 className="font-medium text-gray-900 mb-3">{t('heatPumps.hp1HealthStatus')}</h4>
            <div className="space-y-2">
            </div>
          </div>
          <div>
            <h4 className="font-medium text-gray-900 mb-3">{t('heatPumps.hp2HealthStatus')}</h4>
            <div className="space-y-2">
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
